import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';

export const GalleryEvent = ({ event, delay = 0, onOpen }) => {
  const [shareCopied, setShareCopied] = useState(false);
  const thumbnail = event.thumbnail || event.images?.[0];

  const handleShare = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/studios/${event.id}`;

    const markCopied = () => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    };

    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(markCopied);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      markCopied();
    }
  };

  return (
    <div
      className="gallery-item"
      onClick={() => onOpen(event)}
    >
      <img
        src={thumbnail}
        alt={event.title}
        className="gallery-primary-image"
        loading="lazy"
        decoding="async"
      />
      <div className="gallery-overlay">
        <div className="gallery-overlay-content">
          <span>{event.title}</span>
          <span className="gallery-count">{event.images.length} {event.images.length === 1 ? 'image' : 'images'}</span>
        </div>
      </div>
      <button
        className={`gallery-share-btn${shareCopied ? ' copied' : ''}`}
        onClick={handleShare}
        title={shareCopied ? 'Link copied!' : 'Share this gallery'}
        aria-label="Share gallery"
      >
        {shareCopied ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
        )}
      </button>

    </div>
  );
};
export const GalleryModal = ({ event, isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasMoved, setHasMoved] = useState(false);
  const thumbnailsRef = useRef(null);
  const handleNext = useCallback(() => {
    if (event && event.images) {
      setCurrentIndex((prev) => (prev + 1) % event.images.length);
    }
  }, [event]);
  const handlePrevious = useCallback(() => {
    if (event && event.images) {
      setCurrentIndex((prev) => (prev - 1 + event.images.length) % event.images.length);
    }
  }, [event]);
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const checkOverflow = () => {
      if (thumbnailsRef.current) {
        const hasHorizontalOverflow = thumbnailsRef.current.scrollWidth > thumbnailsRef.current.clientWidth;
        setHasOverflow(hasHorizontalOverflow);
      }
    };
    if (isOpen && thumbnailsRef.current) {
      setTimeout(checkOverflow, 100);

      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }
  }, [isOpen, event]);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen || !event) return;
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, event, onClose, handleNext, handlePrevious]);
  const handleMouseDown = (e) => {
    if (!thumbnailsRef.current) return;
    setIsDragging(true);
    setHasMoved(false);
    setStartX(e.pageX - thumbnailsRef.current.offsetLeft);
    setScrollLeft(thumbnailsRef.current.scrollLeft);
    e.preventDefault();
  };
  const handleMouseMove = (e) => {
    if (!isDragging || !thumbnailsRef.current) return;
    e.preventDefault();
    const x = e.pageX - thumbnailsRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    if (Math.abs(walk) > 3) {
      setHasMoved(true);
    }
    thumbnailsRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setHasMoved(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    setHasMoved(false);
  };
  if (!event) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isOpen ? 1 : 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="gallery-modal"
      onClick={onClose}
      style={{
        pointerEvents: isOpen ? 'auto' : 'none',
        display: 'flex'
      }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="gallery-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gallery-modal-header">
          <h3>{event.title}</h3>
          <span className="gallery-modal-counter">
            {currentIndex + 1} / {event.images.length}
          </span>
          <button className="gallery-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="gallery-modal-image-container">
          {event.images.length > 1 && (
            <button className="gallery-modal-nav gallery-modal-prev" onClick={handlePrevious}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          <motion.img
            key={currentIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            src={event.images[currentIndex]}
            alt={`${event.title} - Image ${currentIndex + 1}`}
            className="gallery-modal-image"
          />
          {event.images.length > 1 && (
            <button className="gallery-modal-nav gallery-modal-next" onClick={handleNext}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
        {event.images.length > 1 && (
          <div className="gallery-modal-thumbnails-container">
            {hasOverflow && (
              <button
                className="gallery-modal-thumb-nav gallery-modal-thumb-prev"
                onClick={() => {
                  if (thumbnailsRef.current) {
                    thumbnailsRef.current.scrollBy({ left: -100, behavior: 'smooth' });
                  }
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            <div
              className={`gallery-modal-thumbnails ${!hasOverflow ? 'centered' : ''} ${isDragging ? 'dragging' : ''}`}
              ref={thumbnailsRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
            >
              {event.images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Thumbnail ${index + 1}`}
                  className={index === currentIndex ? 'active' : ''}
                  onClick={(e) => {
                    if (!hasMoved) {
                      setCurrentIndex(index);
                    }
                    e.stopPropagation();
                  }}
                  onDragStart={(e) => e.preventDefault()}
                />
              ))}
            </div>
            {hasOverflow && (
              <button
                className="gallery-modal-thumb-nav gallery-modal-thumb-next"
                onClick={() => {
                  if (thumbnailsRef.current) {
                    thumbnailsRef.current.scrollBy({ left: 100, behavior: 'smooth' });
                  }
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>

              </button>

            )}
          </div>

        )}
      </motion.div>

    </motion.div>

  );
};

export const GallerySectionWithParallax = ({ children }) => {
  return (
    <section id="gallery" className="section gallery-section" data-scroll-section>
      <div className="container">
        {children}
      </div>

    </section>

  );
};
